import React, { useReducer, useState, useCallback } from "react";
import { Typography, Checkbox, Button } from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { useAppDispatch } from "store/hooks";
import { handleUpdateDealerCareDetailsRequest } from "store/modules/DealerCare/thunk.store";
import { DealerSearchByIdResponse } from "store/modules/DealerCare/types.store";
import * as Styled from "../DealerInfo/styles";
import ModalServiceConfig, { MessageType } from "../ModalSuperRecolhe";
import ModalNoServiceChanges from "../ModalNoServiceChanges";

type ServicesState = {
  superRecolhe: boolean;
  schedulingEnabled: boolean;
  gasDoPovo: boolean;
  onlinePaymentEnabled: boolean;
};

type ServicesAction =
  | { type: "TOGGLE_SUPER_RECOLHE" }
  | { type: "TOGGLE_SCHEDULING_ENABLED" }
  | { type: "TOGGLE_GAS_DO_POVO" }
  | { type: "SET_SCHEDULING_ENABLED"; payload: boolean }
  | { type: "SET_GAS_DO_POVO"; payload: boolean }
  | { type: "TOGGLE_ONLINE_PAYMENT_ENABLED" }
  | { type: "SET_SUPER_RECOLHE"; payload: boolean }
  | { type: "SET_ONLINE_PAYMENT_ENABLED"; payload: boolean }
  | { type: "RESET"; payload: ServicesState };

const servicesReducer: React.Reducer<ServicesState, ServicesAction> = (
  state,
  action,
) => {
  switch (action.type) {
    case "TOGGLE_SUPER_RECOLHE":
      return { ...state, superRecolhe: !state.superRecolhe };
    case "TOGGLE_SCHEDULING_ENABLED":
      return { ...state, schedulingEnabled: !state.schedulingEnabled };
    case "TOGGLE_GAS_DO_POVO":
      return { ...state, gasDoPovo: !state.gasDoPovo };
    case "TOGGLE_ONLINE_PAYMENT_ENABLED":
      return { ...state, onlinePaymentEnabled: !state.onlinePaymentEnabled };
    case "SET_SUPER_RECOLHE":
      return { ...state, superRecolhe: action.payload };
    case "SET_SCHEDULING_ENABLED":
      return { ...state, schedulingEnabled: action.payload };
    case "SET_GAS_DO_POVO":
      return { ...state, gasDoPovo: action.payload };
    case "SET_ONLINE_PAYMENT_ENABLED":
      return { ...state, onlinePaymentEnabled: action.payload };
    case "RESET":
      return action.payload;
    default:
      return state;
  }
};

const ScheduleService =
  "O Agendamento permite o usuário realizar o agendamento da entrega, através do Whatsapp, quando a revenda estiver fora do horário de funcionamento.";

const GasDoPovoService =
  "O Gás do povo permite efetuar a distribuição do beneficio do governo federal, via portaria, whatsapp e app.";

const OnlinePaymentService =
  "O Pagamento Online permite a revenda oferecer o pagamento online para seus clientes.";

type ServiceName =
  | "superRecolhe"
  | "schedulingEnabled"
  | "gasDoPovo"
  | "onlinePaymentEnabled";

export interface DealerServicesProps {
  dealerId: string;
  dealerData: DealerSearchByIdResponse;
  reload: () => Promise<void>;
}

function DealerServices({
  dealerId,
  dealerData,
  reload,
}: DealerServicesProps): JSX.Element {
  const dispatch = useAppDispatch();
  const [isEditingServices, setIsEditingServices] = useState(false);
  const [servicesState, dispatchServices] = useReducer(servicesReducer, {
    superRecolhe: dealerData?.dealerInfo?.attributes?.superRecolhe ?? false,
    schedulingEnabled:
      dealerData?.dealerInfo?.attributes?.schedulingEnabled ?? false,
    gasDoPovo: dealerData?.dealerInfo?.attributes?.gasDoPovo ?? false,
    onlinePaymentEnabled:
      dealerData?.dealerInfo?.attributes?.onlinePaymentEnabled ?? false,
  } as ServicesState);

  const servicesTooltipSlotProps = Styled.getServicesTooltipSlotProps();

  const [isOpenModalServiceConfig, setIsOpenModalServiceConfig] =
    useState(false);
  const [isOpenModalNoServiceChanges, setIsOpenModalNoServiceChanges] =
    useState(false);
  const [currentServiceName, setCurrentServiceName] = useState<string | null>(
    null,
  );
  const [currentMessageType, setCurrentMessageType] =
    useState<MessageType | null>(null);

  const handleServiceToggle = (serviceName: ServiceName) => {
    const newValue = !servicesState[serviceName];

    const toggleActionMap: Record<
      ServiceName,
      | { type: "TOGGLE_SUPER_RECOLHE" }
      | { type: "TOGGLE_SCHEDULING_ENABLED" }
      | { type: "TOGGLE_GAS_DO_POVO" }
      | { type: "TOGGLE_ONLINE_PAYMENT_ENABLED" }
    > = {
      superRecolhe: { type: "TOGGLE_SUPER_RECOLHE" },
      schedulingEnabled: { type: "TOGGLE_SCHEDULING_ENABLED" },
      gasDoPovo: { type: "TOGGLE_GAS_DO_POVO" },
      onlinePaymentEnabled: { type: "TOGGLE_ONLINE_PAYMENT_ENABLED" },
    };

    dispatchServices(toggleActionMap[serviceName]);
    setCurrentServiceName(serviceName);
    setCurrentMessageType(newValue ? "enable" : "disable");
    setIsOpenModalServiceConfig(true);
  };

  const handleSchedulingEnabledToggle = () =>
    handleServiceToggle("schedulingEnabled");

  const handleGasDoPovoToggle = () => handleServiceToggle("gasDoPovo");

  const handleOnlinePaymentEnabledToggle = () =>
    handleServiceToggle("onlinePaymentEnabled");

  const getInitialServicesState = (): ServicesState => ({
    superRecolhe: !!dealerData?.dealerInfo?.attributes?.superRecolhe,
    schedulingEnabled: !!dealerData?.dealerInfo?.attributes?.schedulingEnabled,
    gasDoPovo: !!dealerData?.dealerInfo?.attributes?.gasDoPovo,
    onlinePaymentEnabled:
      !!dealerData?.dealerInfo?.attributes?.onlinePaymentEnabled,
  });

  const handleApplyServiceChanges = async (closeEditMode = false) => {
    const initial = getInitialServicesState();
    const hasNoChanges =
      servicesState.superRecolhe === initial.superRecolhe &&
      servicesState.schedulingEnabled === initial.schedulingEnabled &&
      servicesState.gasDoPovo === initial.gasDoPovo &&
      servicesState.onlinePaymentEnabled === initial.onlinePaymentEnabled;

    if (hasNoChanges) {
      setIsOpenModalNoServiceChanges(true);
      return;
    }

    try {
      await dispatch(
        handleUpdateDealerCareDetailsRequest({
          dealerId,
          tradeName: dealerData?.dealerInfo?.tradeName,
          telephone: {
            number: dealerData?.dealerInfo?.telephone?.number,
            areaCode: dealerData?.dealerInfo?.telephone?.areaCode,
            countryCode: dealerData?.dealerInfo?.telephone?.countryCode,
          },
          cellphone: {
            number: dealerData?.dealerInfo?.cellphone?.number,
            areaCode: dealerData?.dealerInfo?.cellphone?.areaCode,
            countryCode: dealerData?.dealerInfo?.cellphone?.countryCode,
          },
          unit: dealerData?.dealerInfo?.unit,
          subunit: dealerData?.dealerInfo?.subunit,
          whatsAppNumber: dealerData?.dealerInfo?.whatsAppNumber ?? undefined,
          attributes: {
            superRecolhe: !!servicesState.superRecolhe,
            schedulingEnabled: !!servicesState.schedulingEnabled,
            gasDoPovo: !!servicesState.gasDoPovo,
            onlinePaymentEnabled: !!servicesState.onlinePaymentEnabled,
          },
        }),
      ).unwrap();
      await reload();
    } catch {
      toast("Erro ao aplicar alterações nos serviços, tente novamente!", {
        type: "error",
      });
    } finally {
      if (closeEditMode) {
        setIsEditingServices(false);
      }
      setIsOpenModalServiceConfig(false);
      setCurrentServiceName(null);
      setCurrentMessageType(null);
    }
  };

  const handleCancelServiceChange = () => {
    switch (currentServiceName) {
      case "superRecolhe":
        dispatchServices({ type: "TOGGLE_SUPER_RECOLHE" });
        break;
      case "schedulingEnabled":
        dispatchServices({ type: "TOGGLE_SCHEDULING_ENABLED" });
        break;
      case "onlinePaymentEnabled":
        dispatchServices({ type: "TOGGLE_ONLINE_PAYMENT_ENABLED" });
        break;
      case "gasDoPovo":
        dispatchServices({ type: "TOGGLE_GAS_DO_POVO" });
        break;
      default:
        break;
    }
    setIsOpenModalServiceConfig(false);
    setCurrentServiceName(null);
    setCurrentMessageType(null);
  };

  const handleConfirmServiceModal = () => {
    setIsOpenModalServiceConfig(false);
    setCurrentServiceName(null);
    setCurrentMessageType(null);
  };

  const resetServicesFromServer = useCallback(() => {
    dispatchServices({
      type: "RESET",
      payload: {
        superRecolhe: !!dealerData?.dealerInfo?.attributes?.superRecolhe,
        schedulingEnabled:
          !!dealerData?.dealerInfo?.attributes?.schedulingEnabled,
        gasDoPovo: !!dealerData?.dealerInfo?.attributes?.gasDoPovo,
        onlinePaymentEnabled:
          !!dealerData?.dealerInfo?.attributes?.onlinePaymentEnabled,
      },
    });
  }, [dealerData]);

  const renderServicesComponent = () => {
    return (
      <Styled.ServicesListContainer>
        <>
          <Styled.ServicesRow>
            <CheckCircleIcon
              color={
                dealerData?.dealerInfo?.attributes?.gasDoPovo
                  ? "success"
                  : "action"
              }
            />
            <Typography variant="body1" color="#000000">
              Gás do Povo
            </Typography>
          </Styled.ServicesRow>
          <Styled.ServicesRow>
            <CheckCircleIcon
              color={
                dealerData?.dealerInfo?.attributes?.schedulingEnabled
                  ? "success"
                  : "action"
              }
            />
            <Typography variant="body1" color="#000000">
              Agendamento
            </Typography>
          </Styled.ServicesRow>
          <Styled.ServicesRow>
            <CheckCircleIcon
              color={
                dealerData?.dealerInfo?.attributes?.onlinePaymentEnabled
                  ? "success"
                  : "action"
              }
            />
            <Typography variant="body1" color="#000000">
              Pagamento Online
            </Typography>
          </Styled.ServicesRow>
        </>
      </Styled.ServicesListContainer>
    );
  };

  const renderEditServicesComponent = () => {
    return (
      <>
        <Styled.ServicesRow>
          <Checkbox
            checked={servicesState.gasDoPovo}
            onChange={handleGasDoPovoToggle}
          />
          <Typography variant="body1" color="#000000">
            Gás do Povo
          </Typography>
          <Styled.ServicesTooltip
            title={GasDoPovoService}
            placement="right"
            componentsProps={servicesTooltipSlotProps}
          >
            <InfoOutlinedIcon />
          </Styled.ServicesTooltip>
        </Styled.ServicesRow>
        <Styled.ServicesRow>
          <Checkbox
            checked={servicesState.schedulingEnabled}
            onChange={handleSchedulingEnabledToggle}
          />
          <Typography variant="body1" color="#000000">
            Agendamento
          </Typography>
          <Styled.ServicesTooltip
            title={ScheduleService}
            placement="right"
            componentsProps={servicesTooltipSlotProps}
          >
            <InfoOutlinedIcon />
          </Styled.ServicesTooltip>
        </Styled.ServicesRow>
        <Styled.ServicesRow>
          <Checkbox
            checked={servicesState.onlinePaymentEnabled}
            onChange={handleOnlinePaymentEnabledToggle}
          />
          <Typography variant="body1" color="#000000">
            Pagamento Online
          </Typography>
          <Styled.ServicesTooltip
            title={OnlinePaymentService}
            placement="right"
            componentsProps={servicesTooltipSlotProps}
          >
            <InfoOutlinedIcon />
          </Styled.ServicesTooltip>
        </Styled.ServicesRow>
      </>
    );
  };

  return (
    <>
      <Styled.ContainerSuperRecolhe>
        <Typography variant="body2" color="#000000" sx={{ fontSize: 12 }}>
          Serviços
        </Typography>
        <Styled.ServicesContainer>
          {isEditingServices
            ? renderEditServicesComponent()
            : renderServicesComponent()}
        </Styled.ServicesContainer>
        <Styled.ServicesButtonsContainer>
          {isEditingServices ? (
            <Styled.SuperRecolheEditContainer>
              <Button
                variant="outlined"
                sx={{ borderRadius: "999px" }}
                color="inherit"
                onClick={() => {
                  setIsEditingServices(false);
                  resetServicesFromServer();
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                sx={{ borderRadius: "999px" }}
                color="primary"
                onClick={() => handleApplyServiceChanges(true)}
              >
                Aplicar
              </Button>
            </Styled.SuperRecolheEditContainer>
          ) : (
            <Button
              variant="contained"
              sx={{ borderRadius: "999px" }}
              color="primary"
              onClick={() => setIsEditingServices(true)}
            >
              Editar
            </Button>
          )}
        </Styled.ServicesButtonsContainer>
      </Styled.ContainerSuperRecolhe>
      {currentServiceName && currentMessageType && (
        <ModalServiceConfig
          isOpen={isOpenModalServiceConfig}
          serviceName={currentServiceName}
          messageType={currentMessageType}
          onNo={handleCancelServiceChange}
          onYes={handleConfirmServiceModal}
        />
      )}
      <ModalNoServiceChanges
        isOpen={isOpenModalNoServiceChanges}
        onClose={() => setIsOpenModalNoServiceChanges(false)}
      />
    </>
  );
}

export default DealerServices;
